import React from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { routeTree } from "./routeTree";
import { AuthProvider } from "./lib/auth";
import { initFaro, reportError } from "./observability";
import "./index.css";

initFaro();

const router = createRouter({ routeTree });

const GOOGLE_CLIENT_ID =
  (import.meta.env.VITE_GOOGLE_ADMIN_CLIENT_ID as string) ?? "";

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

function ErrorFallback() {
  return (
    <main style={{ padding: "4rem", textAlign: "center" }}>
      <h1>Something went wrong</h1>
      <p>An unexpected error occurred. Please reload the page.</p>
    </main>
  );
}

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: unknown): void {
    reportError(error);
  }

  render(): React.ReactNode {
    return this.state.hasError ? <ErrorFallback /> : this.props.children;
  }
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <AuthProvider>
          <RouterProvider router={router} />
        </AuthProvider>
      </GoogleOAuthProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
